import { format, parseISO } from 'date-fns';
import { UsersThree, DownloadSimple } from '@phosphor-icons/react';
import { fmtInr } from './helpers';

const isJoined = (c) => (c.joining_status || '').toLowerCase() === 'joined';
const effCharge = (c) => (c.charge_override != null ? Number(c.charge_override) : Number(c.consultancy_charge)) || 0;

export const computeMonthSummary = (candidates, month) => {
  const inMonth = (d) => !!month && !!d && d.slice(0, 7) === month;
  let linedUp = 0, joined = 0, pending = 0, fees = 0, collected = 0;
  for (const c of candidates) {
    if (inMonth(c.interview_date)) linedUp += 1;
    if (inMonth(c.date_of_joining)) {
      if (isJoined(c)) { joined += 1; fees += effCharge(c); } else pending += 1;
    }
    for (const p of c.payments || []) {
      if (inMonth(p.collected_date)) collected += Number(p.amount) || 0;
    }
  }
  return { linedUp, joined, pending, fees, collected };
};

export const computeRecruiterSummary = (candidates, month) => {
  const inMonth = (d) => !!month && !!d && d.slice(0, 7) === month;
  const acc = {};
  const bucket = (name) => (acc[name] ||= { recruiter: name, linedUp: 0, joined: 0, pending: 0 });
  for (const c of candidates) {
    const name = (c.recruiter_name || '').trim() || 'Unassigned';
    if (inMonth(c.interview_date)) bucket(name).linedUp += 1;
    if (inMonth(c.date_of_joining)) {
      if (isJoined(c)) bucket(name).joined += 1; else bucket(name).pending += 1;
    }
  }
  return Object.values(acc).sort((a, b) => b.linedUp - a.linedUp || b.joined - a.joined);
};

const Stat = ({ testId, label, value, bg }) => (
  <div data-testid={testId} className="border-2 border-black rounded-md px-3 py-2 min-w-[140px]" style={{ backgroundColor: bg }}>
    <div className="text-lg font-black text-black leading-tight">{value}</div>
    <div className="text-[10px] font-bold uppercase tracking-wider text-black opacity-80">{label}</div>
  </div>
);

export const MonthSummary = ({ candidates, month, isOwner }) => {
  if (!month) return null;
  const s = computeMonthSummary(candidates, month);
  const label = format(parseISO(`${month}-01`), 'MMM yyyy');
  return (
    <div className="mb-4 flex flex-wrap gap-2" data-testid="month-summary">
      <Stat testId="summary-lined-up" label={`Lined up in ${label}`} value={s.linedUp} bg="#93C5FD" />
      <Stat testId="summary-joined" label={`Joined in ${label}`} value={s.joined} bg="#4ADE80" />
      <Stat testId="summary-pending" label="Yet to join" value={s.pending} bg="#FDE047" />
      {isOwner && <Stat testId="summary-fees" label="Fees (joined only)" value={fmtInr(s.fees)} bg="#A3E635" />}
      {isOwner && <Stat testId="summary-collected" label="Collected this month" value={fmtInr(s.collected)} bg="#F9A8D4" />}
    </div>
  );
};

export const RecruiterMonthTable = ({ candidates, month, onExport }) => {
  if (!month) return null;
  const rows = computeRecruiterSummary(candidates, month);
  const label = format(parseISO(`${month}-01`), 'MMM yyyy');
  const totals = rows.reduce((t, r) => ({ linedUp: t.linedUp + r.linedUp, joined: t.joined + r.joined, pending: t.pending + r.pending }), { linedUp: 0, joined: 0, pending: 0 });
  const rate = (r) => (r.linedUp ? `${Math.round((r.joined / r.linedUp) * 100)}%` : '—');
  return (
    <div className="mb-4 bg-[#0F3622] border-2 border-[#93C5FD] rounded-md overflow-x-auto" data-testid="recruiter-month-table">
      <div className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-white flex items-center gap-2">
        <UsersThree size={16} weight="duotone" className="text-[#93C5FD]" /> Recruiter performance — {label}
        <button
          data-testid="recruiter-export-button"
          onClick={onExport}
          className="ml-auto flex items-center gap-1 px-3 py-1 bg-[#93C5FD] text-black border-2 border-black rounded-md text-[10px] font-bold hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all normal-case"
        >
          <DownloadSimple size={14} weight="bold" /> Export Excel
        </button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#143E29] text-[#A3E635] text-[10px] uppercase tracking-wider">
            <th className="text-left px-3 py-1.5">Recruiter</th>
            <th className="text-right px-3 py-1.5">Lined up</th>
            <th className="text-right px-3 py-1.5">Joined</th>
            <th className="text-right px-3 py-1.5">Yet to join</th>
            <th className="text-right px-3 py-1.5">Join rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={5} className="px-3 py-3 text-center text-white/70" data-testid="recruiter-month-empty">No activity in {label}</td></tr>
          ) : rows.map((r) => (
            <tr key={r.recruiter} className="border-t border-[#143E29] text-white font-bold" data-testid={`recruiter-month-row-${r.recruiter}`}>
              <td className="px-3 py-1.5">{r.recruiter}</td>
              <td className="px-3 py-1.5 text-right text-[#93C5FD]">{r.linedUp}</td>
              <td className="px-3 py-1.5 text-right text-[#4ADE80]">{r.joined}</td>
              <td className="px-3 py-1.5 text-right text-[#FDE047]">{r.pending}</td>
              <td className="px-3 py-1.5 text-right">{rate(r)}</td>
            </tr>
          ))}
          {rows.length > 0 && (
            <tr className="border-t-2 border-[#93C5FD] text-black font-black bg-[#93C5FD]" data-testid="recruiter-month-totals">
              <td className="px-3 py-1.5">Total</td>
              <td className="px-3 py-1.5 text-right">{totals.linedUp}</td>
              <td className="px-3 py-1.5 text-right">{totals.joined}</td>
              <td className="px-3 py-1.5 text-right">{totals.pending}</td>
              <td className="px-3 py-1.5 text-right">{rate(totals)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
